import React, { useEffect, useState } from "react";

import { Grid, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { WrapperLabel } from "Components/Form/Input/styles";
import { AppSwitch } from "Components/Generic/AppSwitch";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Button, Input } from "../../../../../../../components/Form";
import { Color } from "../../../../../../../components/Form/Button";
import Skeleton from "../../../../../../../components/Generic/Skeleton";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { SSCC } from "../../../../../../../models/sscc";
import SSCCService from "../../../../../../../services/sscc.service";
import useStyles, { SSCCGenerationInputLabel, SSCCGenerationSwitchWRapper } from "./styles";
import { isMoving } from "Util/validation";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const SSCCGeneration = ({ permission }: Props) => {
  const classes = useStyles();

  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [responseError, setResponseError] = useState<Error>();

  const [hasError, setHasError] = useState<boolean>();

  const [ssccToUpdate, setSSCCToUpdate] = useState<SSCC>();

  const [extension, setExtension] = useState<SSCC["extension"]>("");
  const [gs1Company, setGS1Company] = useState<SSCC["gs1Company"]>("");
  const [serialNumber, setSerialNumber] = useState<string>("");
  const [showSupplierName, setShowSupplierName] = useState<SSCC["showSupplierName"]>(true);

  const ssccObject: SSCC = {
    extension,
    gs1Company,
    serialNumber: Number(serialNumber),
    showSupplierName,
  };

  const rejectNonNumbers = (value: string) => value.replace(/\D/, "");
  const minLengthMessage = (length: number) =>
    `${translate("mustHave")} ${length} ${length === 1 ? translate("digit") : translate("digits")}`;
  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    extension: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .length(1, minLengthMessage(1)),
    gs1Company: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .length(7, minLengthMessage(7)),
    serialNumber: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .length(9, minLengthMessage(9)),
  });

  const isValid = () => {
    try {
      schema.validateSync(
        {
          extension,
          gs1Company,
          serialNumber,
          showSupplierName,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const { refetch, isFetching } = useQuery(
    ["sscc.list"],
    () => SSCCService.getById(Number(currentUser?.companyId)),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setSSCCToUpdate(response.data);
      },
    }
  );

  const { mutate: updateSSCC, isLoading: isUpdating } = useMutation(
    ["sscc.update"],
    ({ id, sscc }: { id: number; sscc: SSCC }) => SSCCService.update(id, sscc),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "request.notification.success.sscc",
        });
        setHasError(false);
        refetch();
      },
      onError: (error: AxiosError<Error>) => {
        setHasError(true);
        if (error && error.response) {
          setResponseError(error?.response?.data);
          NotificationToast.error(error?.response?.data?.message);
        }
      },
    }
  );

  const { mutate: createSSCC, isLoading: isCreating } = useMutation(
    ["sscc.create"],
    (sscc: SSCC) => SSCCService.create(sscc),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        setHasError(false);
        refetch();
      },
      onError: (error: AxiosError<Error>) => {
        setHasError(true);
        if (error && error.response) {
          setResponseError(error?.response?.data);
          NotificationToast.error(error?.response?.data?.message);
        }
      },
    }
  );

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (ssccToUpdate && ssccToUpdate.id) {
      if (ssccToUpdate) {
        const object = {
          ...ssccToUpdate,
          ...ssccObject,
          serialNumber: Number(serialNumber),
        };

        if (isValid()) updateSSCC({ id: ssccToUpdate.id, sscc: object as SSCC });
      }
    } else if (createSSCC) {
      const object = {
        ...ssccObject,
        companyId: currentUser?.companyId,
      } as SSCC;

      if (isValid()) createSSCC(object);
    }
  };

  const handleCancel = () => {
    setExtension(ssccToUpdate?.extension ?? "");
    setGS1Company(ssccToUpdate?.gs1Company ?? "");
    setSerialNumber(ssccToUpdate?.serialNumber.toString().padStart(9, "0") ?? "");
  };

  useEffect(() => {
    if (hasFormBeenSubmitted) isValid();
  }, [
    hasFormBeenSubmitted,
    ...Object.keys(ssccObject).map((key) => ssccObject[key as keyof typeof ssccObject]),
  ]);

  useEffect(() => {
    if (ssccToUpdate) {
      setExtension(ssccToUpdate.extension);
      setGS1Company(ssccToUpdate.gs1Company);
      setSerialNumber(ssccToUpdate.serialNumber.toString().padStart(9, "0"));
      setShowSupplierName(ssccToUpdate.showSupplierName);
    }
  }, [ssccToUpdate]);

  return (
    <div className={classes.ssccWrapper}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography fontSize="16px" fontWeight={700} fontFamily="Manrope">
            {translate("details")}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <Skeleton isLoading={isFetching}>
            <Input
              type="text"
              name="extension"
              label={translate("extensionDigit")}
              error={!!getErrorByField("extension")}
              errorMessage={getErrorByField("extension")}
              errorResponse={responseError}
              value={extension}
              min={0}
              max={9}
              maxLength={1}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setExtension(rejectNonNumbers(event.target.value))
              }
            />
          </Skeleton>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <Skeleton isLoading={isFetching}>
            <Input
              type="text"
              name="gs1Company"
              label={translate("gs1CompanyCode")}
              error={!!getErrorByField("gs1Company")}
              errorMessage={getErrorByField("gs1Company")}
              errorResponse={responseError}
              value={gs1Company}
              maxLength={7}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setGS1Company(rejectNonNumbers(event.target.value))
              }
            />
          </Skeleton>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <Skeleton isLoading={isFetching}>
            <Input
              type="text"
              name="serialNumber"
              label={translate("serialNumber")}
              error={!!getErrorByField("serialNumber")}
              errorMessage={getErrorByField("serialNumber")}
              errorResponse={responseError}
              value={serialNumber}
              maxLength={9}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSerialNumber(String(Number(rejectNonNumbers(event.target.value))))
              }
            />
          </Skeleton>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <Skeleton isLoading={isFetching}>
            <SSCCGenerationSwitchWRapper>
              <WrapperLabel>
                <SSCCGenerationInputLabel>
                  {translate("showSupplierNameOnTheSSCCLabel")}
                </SSCCGenerationInputLabel>
              </WrapperLabel>
              <AppSwitch
                checked={showSupplierName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setShowSupplierName(event.target.checked);
                }}
              />
            </SSCCGenerationSwitchWRapper>
          </Skeleton>
        </Grid>

        <Grid container item className={classes.ssccFooter} justifyContent="flex-end">
          <Grid item>
            <Button
              className={classes.button}
              disabled={isFetching || isUpdating || isCreating}
              customColor={Color.CANCEL}
              onClick={() => handleCancel()}
            >
              {translate("cancel")}
            </Button>
          </Grid>
          {
            (
              (ssccToUpdate && ssccToUpdate?.id && permission && isShowAction(currentUser, permission?.edit)) ||
              (ssccToUpdate && !ssccToUpdate?.id && permission && isShowAction(currentUser, permission?.create)) ||
              !isMoving(currentUser?.company)
            ) && <Grid item>
              <Button
                data-cy="saveButton"
                className={classes.button}
                variant="contained"
                customColor={Color.SUCCESS}
                loading={isUpdating || isCreating}
                disabled={isFetching || hasError}
                onClick={() => handleSubmit()}
              >
                {translate("save")}
              </Button>
            </Grid>
          }
        </Grid>
      </Grid>
    </div>
  );
};

export default SSCCGeneration;
